import { ErrorMessage } from "@/components/ErrorMessage";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Text } from "@/components/podkit/typography/Text";
import { useRunnerConfigurationSchema } from "@/queries/runner-configuration-queries";
import { EnvironmentClassesSection } from "@/components/runners/details/EnvironmentClasses";
import { RunnerSharing } from "@/components/runners/details/RunnerSharing";
import { SourceControlProviderSection } from "@/components/runners/details/SourceControlProvider";
import type { PlainMessage } from "@bufbuild/protobuf";
import { RunnerPhase, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useCallback, type FC } from "react";
import { TrackLocations } from "@/hooks/use-segment";
import { Tooltip } from "@/components/Tooltip";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { RunnerActionsDropdown } from "@/components/runners/RunnerActionsDropdown";
import type { PlainRunner } from "@/queries/runner-queries";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/flexkit/Button";
import { MoreHorizontalIcon } from "lucide-react";
import { RunnerProviderSetup } from "@/components/runners/details/RunnerProviderSetup";

export const RunnerDetails: FC<{ runner: PlainMessage<Runner> | undefined }> = ({ runner }) => {
    const { data: schema, isLoading, isPending, error } = useRunnerConfigurationSchema(runner?.runnerId);
    const navigate = useNavigate();

    const schemaLoading = isLoading || isPending;
    const hasSignedUp = runner?.status?.phase !== RunnerPhase.CREATED;

    const onDeleteSuccessful = useCallback(() => {
        navigate("/settings/runners");
    }, [navigate]);

    return (
        <div className="flex size-full flex-col gap-6" data-testid="runner-details">
            <RunnerMetadata runner={runner} onDeleteSuccessful={onDeleteSuccessful} />
            <RunnerProviderSetup runner={runner} hasSignedUp={hasSignedUp} />
            <SkeletonBlock ready={!schemaLoading} failed={Boolean(error)} className="h-[255px]">
                <FailedToLoadSchema error={error} />
                {runner && (
                    <SourceControlProviderSection
                        runner={runner}
                        schemas={schema?.scm}
                        hasStack={hasSignedUp}
                        data-track-location={(modalState) =>
                            modalState.type == "add-provider"
                                ? TrackLocations.AddSCMProviderModal
                                : modalState.type == "remove-provider"
                                  ? TrackLocations.DeleteSCMProviderModal
                                  : TrackLocations.EditSCMProviderModal
                        }
                    />
                )}
                {runner && (
                    <EnvironmentClassesSection
                        runner={runner}
                        schema={schema?.environmentClasses}
                        hasSignedUp={hasSignedUp}
                        data-track-location={(modalState) =>
                            modalState.type === "add"
                                ? TrackLocations.AddEnvironmentClassModal
                                : TrackLocations.EditEnvironmentClassModal
                        }
                    />
                )}
                {runner && <RunnerSharing runnerId={runner.runnerId} hasSignedUp={hasSignedUp} />}
            </SkeletonBlock>
        </div>
    );
};

const FailedToLoadSchema: FC<{ error: Error | null }> = ({ error }) => {
    if (!error) {
        return null;
    }
    return (
        <div className="flex flex-col items-center">
            <Text className="text-lg font-bold text-content-negative">Failed to load runner schema</Text>
            <Text className="text-base text-content-negative">
                Please try to refresh the page and contact support if the issue persists.
            </Text>
            <ErrorMessage error={error} />
        </div>
    );
};

const RunnerMetadata: FC<{
    runner?: PlainRunner | undefined;
    onDeleteSuccessful: () => void;
}> = ({ runner, onDeleteSuccessful }) => {
    const ready = Boolean(runner);
    return (
        <div className={cn("flex flex-col justify-between", "md:flex-row md:items-center")}>
            <SkeletonText ready={ready} size="lg" className="w-28">
                {runner && (
                    <div className="flex items-center gap-2">
                        <Text className="text-xl font-bold">{runner.name}</Text>
                        <Text className="text-xl text-content-secondary">{runner.spec?.configuration?.region}</Text>
                    </div>
                )}
            </SkeletonText>
            <div className="flex items-center gap-2">
                <SkeletonText ready={ready} size="base" className="w-16">
                    <Tooltip content="Runner version">
                        <Text muted={true} className="text-base">
                            {runner?.status?.version}
                        </Text>
                    </Tooltip>
                </SkeletonText>
                <SkeletonText ready={ready} size="base" className="w-10">
                    {runner && <RunnerPhaseTag runner={runner} />}
                </SkeletonText>
                {runner && (
                    <RunnerActionsDropdown
                        runner={runner}
                        onDeleteSuccessful={onDeleteSuccessful}
                        showViewDetails={false}
                        button={
                            <Button
                                variant="ghost"
                                className="h-6 w-6 border-0 p-0"
                                data-testid="runner-actions-dropdown"
                                aria-label="More actions"
                            >
                                <MoreHorizontalIcon className="h-5 w-5 text-content-primary" />
                            </Button>
                        }
                    />
                )}
            </div>
        </div>
    );
};
